import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { toPortalFailure, type PortalFailure } from "../lib/errors";

/**
 * The account's own sessions: plan.md section 4, "View recent or active sessions and revoke other
 * sessions", and two of the five paths section 12 requires to be tested before launch.
 *
 * Three things live here and each of them is one half of a pair whose other half is in the database.
 *
 * **The read is a gateway since 2026-09-13, and it was direct before.** `public.user_session_devices`
 * is granted to `authenticated` and pinned to the owning user, so a direct read was enough while the
 * list only had to say where the account had been seen. It now also has to say which of those
 * sessions are closed, and that fact lives in `auth.sessions`, which no browser role may read.
 * `public.my_session_devices` takes no argument and answers both.
 *
 * **The write at sign-in is a gateway, and it is not called from a screen.** `record_session_device`
 * derives both the account and the session from the JWT, and the browser sends only three
 * descriptive strings plus a random installation key. That key is not a fingerprint: a private
 * window or a cleared browser has no old value to send and is therefore a new device.
 * Where it is called from is `app/auth/SessionProvider.tsx`, which is the one place that observes
 * every way a session comes into existence.
 *
 * **The revocation is two calls in a fixed order, and the order is the safety property.** Supabase
 * Auth ends the sessions and this database only records that they ended, so the logout goes first.
 * The other way round, a failure between them would leave a list saying a session is dead while it
 * is alive, which is the one direction this pair must never fail in. The reverse failure, sessions
 * genuinely ended and the list not yet updated, is recoverable by reloading and is what the panel
 * says happened.
 *
 * **No IP address is read, sent or stored.** `schema.md` section 5 forbids it on this table, and
 * there is no argument on the gateway one could arrive in. What is sent is three strings derived
 * from the user-agent, none of which narrows to a machine.
 */

export const sessionDevicesKey = ["portal", "session-devices"] as const;
const DEVICE_KEY_STORAGE_KEY = "petrovic.portal.deviceKey.v1";

/**
 * One row of `public.my_session_devices()`.
 *
 * There is no `is_current` field and there will not be one. `schema.md` section 5: "'Current device'
 * is derived at request time by comparing `auth_session_id` with the JWT `session_id`; it is not
 * persisted as a boolean that can become stale." The panel does that comparison at render time.
 *
 * `ended` is the opposite case, and it is computed rather than stored for the same reason. The
 * gateway answers it from GoTrue's own record of which sessions exist, so a session a password change
 * closed reads as closed on the next read. `ended_at` is the time the tick found it closed and is
 * null for the minute before it does.
 */
export interface SessionDevice {
  id: string;
  /**
   * Set only on a signed-in row that stands for one session. A folded row and every signed-out row
   * stand for several and carry null, so nothing can mistake one of them for this session.
   */
  auth_session_id: string | null;
  device_label: string | null;
  browser_family: string | null;
  operating_system: string | null;
  first_seen_at: string;
  last_seen_at: string;
  ended: boolean;
  ended_at: string | null;
  /**
   * Whether the row carries a device key. A signed-in row without one is either this session, which
   * is always its own row, or the one folded row standing for every other session that began
   * before device identity existed on 2026-09-15.
   */
  identified: boolean;
  /** How many recorded sign-ins the row stands for. One for a signed-in device. */
  login_count: number;
}

/* ----------------------------------------------------- describing a device */

export interface DeviceDescription {
  deviceLabel: string;
  browserFamily: string;
  operatingSystem: string;
}

export interface DeviceInput {
  userAgent: string;
  /**
   * `navigator.maxTouchPoints`. The only thing separating an iPad from a MacBook in a user-agent
   * string since iPadOS 13, for the reason `app/pwa/platform.ts` states about the same value.
   */
  maxTouchPoints: number;
}

/**
 * A form factor, a browser family and an operating system, and nothing else.
 *
 * Pure, and taking every value as an argument, for the reason `app/pwa/platform.ts` gives about
 * itself: these three strings are stored and then shown to a customer, and a sentence shown to a
 * customer has to be testable without a browser.
 *
 * **What this deliberately does not try to be.** It is not a fingerprint and it must not become one.
 * plan.md section 8 and `schema.md` section 5 keep IP addresses off this table, and the reason
 * generalises: the list exists so a person can recognise their own devices, not so the portal can
 * tell two of them apart. So there is no version number, no screen size, no language, no timezone
 * and no canvas anything. Three coarse buckets are enough to answer "is one of these not mine",
 * which is the only question the list is for.
 *
 * **The order of the browser tests is the whole of their correctness.** Every Chromium browser
 * claims to be Chrome and Safari, Edge claims to be Chrome, and Chrome claims to be Safari. So the
 * most specific claim is tested first and the most general last, and Safari is only reached by a
 * string that claimed none of the others. Getting this backwards labels every device Safari, which
 * is a list that looks right and identifies nothing.
 *
 * A string this function cannot place answers "Okänd" rather than guessing. A device row that says
 * the browser is unknown is honest; one that says Safari because nothing else matched is a fact the
 * reader would act on.
 */
export function describeDevice(input: DeviceInput): DeviceDescription {
  const agent = input.userAgent ?? "";

  const iPadAsMac = /Macintosh/.test(agent) && (input.maxTouchPoints ?? 0) > 1;
  const tablet = /iPad|Tablet/.test(agent) || iPadAsMac;
  const mobile = !tablet && /Mobi|iPhone|iPod|Android/.test(agent);

  const deviceLabel = tablet
    ? "Surfplatta"
    : mobile
      ? "Mobil"
      : agent.length > 0
        ? "Dator"
        : "Okänd enhet";

  const browserFamily = /Edg\//.test(agent)
    ? "Edge"
    : /OPR\/|Opera/.test(agent)
      ? "Opera"
      : /SamsungBrowser/.test(agent)
        ? "Samsung Internet"
        : /Firefox\/|FxiOS/.test(agent)
          ? "Firefox"
          : /Chrome\/|CriOS/.test(agent)
            ? "Chrome"
            : /Safari\//.test(agent)
              ? "Safari"
              : "Okänd";

  const operatingSystem = /iPhone|iPad|iPod/.test(agent) || iPadAsMac
    ? "iOS"
    : /Android/.test(agent)
      ? "Android"
      : /Windows/.test(agent)
        ? "Windows"
        : /CrOS/.test(agent)
          ? "ChromeOS"
          : /Macintosh|Mac OS X/.test(agent)
            ? "macOS"
            : /Linux/.test(agent)
              ? "Linux"
              : "Okänd";

  return { deviceLabel, browserFamily, operatingSystem };
}

/**
 * The same question asked of the real browser, with every value guarded.
 *
 * A runtime with no `navigator` answers the unknown shape instead of throwing on the first property
 * access, which is the service worker, a prerender and every test that has not stubbed one.
 */
export function readDeviceDescription(): DeviceDescription {
  if (typeof navigator === "undefined") {
    return { deviceLabel: "Okänd enhet", browserFamily: "Okänd", operatingSystem: "Okänd" };
  }

  return describeDevice({
    userAgent: navigator.userAgent ?? "",
    maxTouchPoints: navigator.maxTouchPoints ?? 0
  });
}

function randomDeviceKey(): string | null {
  if (typeof crypto === "undefined" || typeof crypto.getRandomValues !== "function") {
    return null;
  }

  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);

  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * A browser-installation key, and nothing about the device.
 *
 * This is the line between "same browser" and fingerprinting. The value is random, stored only in
 * this browser's local storage, and scoped to this portal. The database stores only a hash derived
 * from it and the account id, so the key is not a person detail and not portable between accounts.
 */
export function readDeviceKey(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const existing = window.localStorage.getItem(DEVICE_KEY_STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const next = randomDeviceKey();
    if (!next) {
      return null;
    }

    window.localStorage.setItem(DEVICE_KEY_STORAGE_KEY, next);
    return next;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------- the gateway */

/**
 * Records the session this browser is on, and never makes the caller wait on the answer.
 *
 * Returns whether the write landed rather than throwing, because the one caller is
 * `app/auth/SessionProvider.tsx` and a session must not fail to be established because a device row
 * could not be written. What a failure costs is one row missing from a list, and the person can
 * still sign in, read their tenant and end every other session.
 *
 * It is a plain function and not a hook on purpose. The call happens inside an auth-state
 * subscription, which is not a render, and a mutation hook there would be a hook called from a
 * callback.
 */
export async function recordSessionDevice(): Promise<boolean> {
  const description = readDeviceDescription();
  const deviceKey = readDeviceKey();

  try {
    const { error } = await getSupabaseBrowserClient().rpc("record_session_device", {
      p_device_label: description.deviceLabel,
      p_browser_family: description.browserFamily,
      p_operating_system: description.operatingSystem,
      p_device_key: deviceKey ?? undefined
    });

    return !error;
  } catch {
    // `rpc` rejects on a transport failure rather than returning one. A browser that came back from
    // sleep with no network signs in from its stored session and reaches this line, and the answer
    // is the same as any other failure: the row is not there, and nothing else changes.
    return false;
  }
}

/* ---------------------------------------------------------------- the list */

/**
 * Every live session of this account, then the most recent ones that have ended.
 *
 * **Revised 2026-09-19: the gateway answers in the shape the panel shows.** Signed-in rows are one
 * per device, with every keyless session other than this one folded into one row, and signed-out
 * rows are grouped by device kind, browser and system with `login_count` saying how many sign-ins
 * each stands for. Migration 20260919010000 says why the grouping is on the server: the cap of
 * twenty closed rows is applied there, and grouping after the cap would count wrong.
 *
 * **Ended sessions are shown, and until 2026-09-13 they were filtered out.** The reasoning then was
 * that a session that has ended is not something a person can act on. It is something a person reads,
 * and the person reading is the one who suspects an intrusion and needs to see which sessions are
 * actually closed. Worse, the filter was on `revoked_at`, which only `Logga ut andra enheter` ever
 * wrote: a phone signed out by a password change stayed listed as active, with nothing on the row to
 * say otherwise, while a session the portal itself had closed vanished.
 *
 * **Why a gateway and no longer a direct read.** Whether a session is closed is a fact GoTrue holds
 * in `auth.sessions`, which no browser role may read, so the table alone cannot answer it.
 * `public.my_session_devices` asks GoTrue's record and returns the answer beside each row; it takes
 * no argument, so it is not a way for one account to read another's. Live sessions first, then the
 * twenty most recently active closed ones, which is the gateway's order and is not repeated here.
 */
export function useSessionDevices(enabled: boolean): UseQueryResult<SessionDevice[]> {
  return useQuery({
    queryKey: sessionDevicesKey,
    enabled,
    queryFn: async () => {
      const { data, error } = await getSupabaseBrowserClient().rpc("my_session_devices");

      if (error) {
        throw error;
      }

      return (data ?? []) as SessionDevice[];
    }
  });
}

/* ----------------------------------------------------------- the revocation */

/**
 * Ends every session but this one, then marks the rows that were ended.
 *
 * `signOutOthers` is passed in rather than reached for, because the only client that may touch auth
 * state is the singleton behind `useSession`, and `app/auth/SessionProvider.tsx` already owns the
 * `others` scope with the comment explaining why the default is `global`. Taking it as an argument
 * keeps that one owner and lets a test drive this without an auth client.
 *
 * **The failure between the two calls is reported and not swallowed.** Supabase Auth has ended the
 * sessions by the time the marking runs, so a failure there does not put them back and the caller
 * must not be told the action failed. It is told the second half failed, which is a different
 * sentence, and `PORTAL_SESSIONS_ENDED_LIST_STALE` is the code the panel renders for it. Dropping
 * the error would leave a list quietly describing sessions that no longer exist.
 *
 * That code is one of the two entries in `app/lib/errors.ts` the client decides for itself rather
 * than receiving from a server, the way `PORTAL_OFFLINE` already is. It is raised as an `Error`
 * whose message is the code, which is the shape `toPortalFailure` reads, so the sentence stays in
 * the one file that owns every sentence and this one does not invent its own wording.
 */
export const SESSION_LIST_STALE = "PORTAL_SESSIONS_ENDED_LIST_STALE";

export function useRevokeOtherSessions(signOutOthers: () => Promise<void>) {
  const queryClient = useQueryClient();

  return useMutation<number, PortalFailure, void>({
    mutationFn: async () => {
      try {
        await signOutOthers();
      } catch (error) {
        // Nothing was ended, so this one keeps the ordinary vocabulary: an offline browser says it
        // is offline and anything else is the unexpected code. Every failure leaves this function
        // as a `PortalFailure` so the panel renders a sentence rather than asking `toPortalFailure`
        // a second time, which would be asking it to read a code out of a Swedish sentence.
        throw toPortalFailure(error);
      }

      // No second argument. The gateway takes none, so the generated `Args` is `never` and passing
      // an empty object is a type error rather than a harmless formality. That is also the property
      // the isolation proof rests on: there is no parameter through which one account could name
      // another's sessions.
      const { data, error } = await getSupabaseBrowserClient().rpc("revoke_other_session_devices");

      if (error) {
        throw toPortalFailure(new Error(SESSION_LIST_STALE));
      }

      return typeof data === "number" ? data : 0;
    },
    // Invalidated on success and on failure. The failure case is the one that needs it most: the
    // sessions are gone either way, and a refetch is what closes the gap when the marking landed
    // after all or when another tab's call did it. Returned since build round 56, so "utloggade"
    // appears with the list that no longer holds them.
    onSettled: () => queryClient.invalidateQueries({ queryKey: sessionDevicesKey })
  });
}
